<template>
  <card-container
    :id="pageId"
    :disabled="isDisabledSubmit"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      {{ formTitle }}
    </template>

    <template #content>
      <h6 class="sub-section-title">
        General Information
      </h6>

      <fieldset>
        <div class="row">
          <label class="col-sm-3 control-label pt-10"> Name* </label>
          <div class="col-sm-3 mobile-pb-10">
            <sanitized-input
              id="name"
              v-model="requestBody.name"
              placeholder="GoGoGrandparent"
              required
            />
          </div>

          <label class="col-sm-2 control-label pt-10"> Code Name* </label>
          <div class="col-sm-3">
            <sanitized-input
              id="codeName"
              v-model="requestBody.codeName"
              placeholder="gogo"
              required
            />
            <span class="help-block"> Please use only characters and "-". </span>
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Extension* </label>
          <div class="col-sm-3">
            <sanitized-input
              id="extension"
              v-model="requestBody.extension"
              placeholder="123456"
              min="10"
              max="999999"
              required
            />
          </div>
          <label class="col-sm-2 control-label"> Promo Code </label>
          <div class="col-sm-3">
            <sanitized-input
              id="promoCode"
              v-model="requestBody.promoCode"
              placeholder="PROMO"
            />
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Phone Number </label>
          <phone-number-input
            id="phone-number"
            v-model="requestBody.phoneNumber"
            class="col-sm-3"
            :page-id="pageId"
          />
          <label class="col-sm-2 control-label"> GoGo Number </label>
          <phone-number-input
            id="phone-number"
            v-model="requestBody.gogoNumber"
            class="col-sm-3"
            :page-id="pageId"
          />
        </div>

        <div class="row">
          <label class="col-sm-3 control-label pt-10"> Email </label>
          <div class="col-sm-3">
            <email-input
              v-model="requestBody.email"
              @update-validation="updateEmailValidation"
            />
          </div>
          <label class="col-sm-2 control-label"> Currency </label>
          <div class="col-sm-3">
            <dropdown-list
              v-model="requestBody.currency"
              :options="currencyTypes"
            />
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Logo </label>
          <div class="col-sm-8">
            <input
              type="file"
              class="form-control"
              accept="image/*"
              @change="chooseLogoImg"
            >
          </div>
        </div>

        <div class="row">
          <label class="col-sm-3 control-label"> Description </label>
          <div class="col-sm-8">
            <sanitized-textarea
              id="description"
              v-model="requestBody.description"
              placeholder="Description"
              autosize
            />
          </div>
        </div>
      </fieldset>

      <h6 class="sub-section-title">
        Company Address
      </h6>

      <expand-address-section
        v-model="address"
        map-type-id="satellite"
        class="mobile-mt-0"
        is-editing
        :parent-id="pageId"
        :show-geocode="false"
        :show-home-note="false"
        :zoom="20"
      />

      <h6 class="sub-section-title">
        User Password
      </h6>
      <fieldset>
        <password-fields-container
          v-model:password="requestBody.password"
          v-model:confirm-password="requestBody.passwordConfirm"
          :required="false"
          @update-validation="updateUserPasswordValidation"
        />
      </fieldset>

      <h6 class="sub-section-title">
        Admin Password
      </h6>
      <fieldset>
        <password-fields-container
          v-model:password="requestBody.adminPassword"
          v-model:confirm-password="requestBody.adminPasswordConfirm"
          :required="false"
          @update-validation="updateAdminPasswordValidation"
        />
      </fieldset>
    </template>

    <template #submit-button>
      {{ submitBtnText }}
    </template>

    <template #additional-buttons>
      <p-button
        type="danger"
        round
        @click="backToList"
      >
        Cancel
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { onMounted, nextTick, ref, Ref, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { currencyTypes } from '@/constants/app';
import { CustomLocationType } from '@/models/address';
import { AffiliateInput, Affiliate } from '@/models/affiliate';
import { affiliateService } from '@/services/affiliate';

const pageId = 'create-update-affiliate';

const route = useRoute();
const router = useRouter();

const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

type AffiliateForm = AffiliateInput & {
  passwordConfirm?: string;
  adminPasswordConfirm?: string;
  fileType?: string;
  description: string;
};

const requestBody: Ref<AffiliateForm> = ref({
  name: '',
  codeName: '',
  phoneNumber: '',
  gogoNumber: '',
  extension: 0,
  promoCode: null,
  email: '',
  currency: currencyTypes[0].key,
  description: '',
  password: '',
  adminPassword: '',
  logo: null,
  imgType: '',
});

const originalAffiliateInfo: Ref<Affiliate | null> = ref(null);

const address: Ref<CustomLocationType> = ref({
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  latitude: null,
  longitude: null,
});

const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  isUserPasswordValid: true,
  isAdminPasswordValid: true,
  isEmailValid: true,
});

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(requestBody.value.phoneNumber, false));
const isGoGoPhoneNumberValid = computed(() => util.isPhoneNumberValid(requestBody.value.gogoNumber, false));

const isDisabledSubmit = computed(() => {
  if (!isPhoneNumberValid.value || !isGoGoPhoneNumberValid.value) {
    return true;
  }

  if (!status.isUserPasswordValid || !status.isAdminPasswordValid) {
    return true;
  }

  if (!status.isEmailValid) {
    return true;
  }

  return false;
});

const formTitle = computed(() => (originalAffiliateInfo.value?.id ? 'Update Affiliate' : 'Create Affiliate'));
const submitBtnText = computed(() => (originalAffiliateInfo.value?.id ? 'Update' : 'Create'));

const updateUserPasswordValidation = (value: boolean) => {
  status.isUserPasswordValid = value;
};

const updateAdminPasswordValidation = (value: boolean) => {
  status.isAdminPasswordValid = value;
};

const updateEmailValidation = (value: boolean) => {
  status.isEmailValid = value;
};

const chooseLogoImg = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;

  if (!files || !files[0]) {
    requestBody.value.logo = null;
    requestBody.value.fileType = '';
    return;
  }

  const fileType = files[0].type;
  if (!fileType || !fileType.startsWith('image')) {
    showErrorPopupDialog({
      text: 'Please choose image file',
    });
    requestBody.value.logo = null;
    requestBody.value.fileType = '';
    return;
  }

  requestBody.value.fileType = fileType;
  convertFileToBase64(files[0]);
};

const convertFileToBase64 = (fileObject: File) => {
  const reader = new FileReader();

  status.isLoading = true;

  reader.readAsDataURL(fileObject);

  reader.onload = (e) => {
    requestBody.value.logo = e?.target?.result?.toString();
    status.isLoading = false;
  };

  reader.onerror = () => {
    status.isLoading = false;
  };
};

const getAffiliateInfo = async (codeName: string) => {
  status.isLoading = true;
  status.spinnerMessage = 'Loading Affiliate...';

  try {
    originalAffiliateInfo.value = await affiliateService.fetchAffiliate(codeName);
    resetForm();
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    await showErrorPopupDialog({
      text: errorText,
    });

    backToList();
  }
  status.isLoading = false;
};

const submitForm = () => {
  const codeNameRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const promoCode = requestBody.value.promoCode?.trim();
  const data: AffiliateInput = {
    name: requestBody.value.name,
    codeName: requestBody.value.codeName.trim(),
    extension: Number(requestBody.value.extension),
    promoCode: promoCode || null,
    phoneNumber: util.getValidPhoneNumber(requestBody.value.phoneNumber ?? ''),
    gogoNumber: util.getValidPhoneNumber(requestBody.value.gogoNumber ?? ''),
    email: requestBody.value.email || null,
    streetAddress: address.value.streetAddress,
    streetAddress2: address.value.streetAddress2,
    city: address.value.city,
    state: address.value.state,
    zip: address.value.zip,
    country: address.value.country,
    latitude: address.value.latitude,
    longitude: address.value.longitude,
    currency: requestBody.value.currency,
    timeZone: requestBody.value.timeZone,
    description: requestBody.value.description,
    isDisabled: requestBody.value.isDisabled,
    logo: requestBody.value.logo,
    imgType: requestBody.value.fileType,
  };

  if (requestBody.value.password) {
    data.password = requestBody.value.password;
  }

  if (requestBody.value.adminPassword) {
    data.adminPassword = requestBody.value.adminPassword;
  }

  const isValidCodeName = codeNameRegex.test(data.codeName);
  if (!isValidCodeName) {
    showErrorPopupDialog({
      text: 'Please use only characters and "-"',
      title: 'Invalid Code Name',
    });
    return;
  }

  if (originalAffiliateInfo.value?.id) {
    updateAffiliate(data);
  } else {
    createAffiliate(data);
  }
};

const createAffiliate = async (data: AffiliateInput) => {
  status.isLoading = true;
  status.spinnerMessage = 'Creating Affiliate...';

  try {
    await affiliateService.createAffiliate(data);
    await showSuccessPopupDialog({
      text: 'The affiliate has been created successfully.',
    });

    backToList();
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  status.isLoading = false;
};

const updateAffiliate = async (data: AffiliateInput) => {
  status.isLoading = true;
  status.spinnerMessage = 'Updating Affiliate...';

  try {
    await affiliateService.updateAffiliate({
      data,
      codeName: originalAffiliateInfo.value?.codeName!,
    });
    await showSuccessPopupDialog({
      text: 'The affiliate has been updated successfully.',
    });

    backToList();
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  status.isLoading = false;
};

const resetForm = () => {
  requestBody.value = {
    name: originalAffiliateInfo.value?.name || '',
    codeName: originalAffiliateInfo.value?.codeName || '',
    phoneNumber: originalAffiliateInfo.value?.phoneNumber || '',
    gogoNumber: originalAffiliateInfo.value?.gogoNumber || '',
    extension: originalAffiliateInfo.value?.extension!,
    promoCode: originalAffiliateInfo.value?.promoCode || null,
    email: originalAffiliateInfo.value?.email || '',
    currency: originalAffiliateInfo.value?.currency || currencyTypes[0].key,
    description: originalAffiliateInfo.value?.description || '',
    password: '',
    adminPassword: '',
  };

  address.value = {
    streetAddress: originalAffiliateInfo.value?.streetAddress || '',
    streetAddress2: originalAffiliateInfo.value?.streetAddress2 || '',
    city: originalAffiliateInfo.value?.city || '',
    state: originalAffiliateInfo.value?.state || '',
    zip: originalAffiliateInfo.value?.zip || '',
    country: originalAffiliateInfo.value?.country || '',
    latitude: originalAffiliateInfo.value?.latitude || null,
    longitude: originalAffiliateInfo.value?.longitude || null,
  };
};

const backToList = () => {
  router.push('/operator/affiliate');
};

onMounted(async () => {
  const hash = route.hash;

  await nextTick();
  util.moveScrollComponent(hash);

  const codeName = (route.params.codeName as string) || '';

  if (codeName) {
    getAffiliateInfo(codeName);
  }
});
</script>
